import React, { useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import styles from "./BotHero.module.css";

/* Tiny inline icons (no extra deps) */
const Svg = (p) => (
  <svg viewBox="0 0 24 24" width="1em" height="1em" stroke="currentColor" fill="none" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" {...p} />
);
const Sparkles = (p) => (
  <Svg {...p}>
    <path d="M12 2l1.5 4.5L18 8l-4.5 1.5L12 14l-1.5-4.5L6 8l4.5-1.5L12 2z" />
    <path d="M20 12l.8 2.4L24 15l-2.4.6L20 18l-.6-2.4L17 15l2.4-.6L20 12z" />
    <path d="M5 14l.6 1.8L8 16l-1.8.4L5 18l-.4-1.6L3 16l1.6-.4L5 14z" />
  </Svg>
);
const ArrowRight = (p) => (
  <Svg {...p}>
    <path d="M5 12h14" />
    <path d="M13 6l6 6-6 6" />
  </Svg>
);

export default function BotHero({ onGetStarted, onSeeHowItWorks }) {
  // 0: Chaos text, 1: NO MORE, 2: Chimp intro
  const [phase, setPhase] = useState(-1); // don't render phases until we start
  const [seq, setSeq] = useState(0);      // force remounts to replay animations
  const heroRef = useRef(null);
  const timers = useRef({ t1: null, t2: null });
  const startedRef = useRef(false);
  const isOverlayPhase = phase === 0 || phase === 1;

  const chaosWords = useMemo(
    () => [
      "endless scrolling",
      "generic job alerts",
      "networking spam",
      "influencer noise",
      "one-size-fits-all advice",
      "information overload",
    ],
    []
  );

  // Timing
  const CHAOS_BASE_DELAY = 1.2; // first item delay (s)
  const CHAOS_STEP = 0.3;       // stagger between items (s)
  const CHAOS_ANIM = 0.5;       // each item anim duration (s)
  const EXTRA_BUFFER = 2.0;     // wait after last word (s)
  const NO_MORE_DURATION = 3.0; // how long to show "NO MORE" (s)

  const clearTimers = () => {
    if (timers.current.t1) clearTimeout(timers.current.t1);
    if (timers.current.t2) clearTimeout(timers.current.t2);
    timers.current.t1 = null;
    timers.current.t2 = null;
  };

  const startSequence = () => {
    clearTimers();
    setSeq((s) => s + 1);
    setPhase(0);
    const lastIndex = chaosWords.length - 1;
    const chaosDone = CHAOS_BASE_DELAY + lastIndex * CHAOS_STEP + CHAOS_ANIM;
    const toPhase1 = (chaosDone + EXTRA_BUFFER) * 1000;
    const toPhase2 = toPhase1 + NO_MORE_DURATION * 1000;

    timers.current.t1 = setTimeout(() => setPhase(1), toPhase1);
    timers.current.t2 = setTimeout(() => setPhase(2), toPhase2);
  };

  // Restart animation whenever this section enters viewport
  useEffect(() => {
    const node = heroRef.current;
    if (!node) return;

    const io = new IntersectionObserver(
      (entries) => {
        const e = entries[0];
        if (e.isIntersecting && e.intersectionRatio >= 0.6 && !startedRef.current) {
          startedRef.current = true;
         startSequence();
        } else if (!e.isIntersecting) {
          // reset so it can run again next time you scroll back
          startedRef.current = false;
         clearTimers();
        }
      },
      { threshold: [0, 0.6, 1] }
    );

    io.observe(node);
    return () => {
      io.disconnect();
      clearTimers();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fallbacks if callbacks not passed
  const handleGetStarted = () => {
    if (onGetStarted) return onGetStarted();
    try { window.scrollTo({ top: 0, behavior: "smooth" }); } catch {}
    window.location.assign("/?auth=login");
  };
  const handleSeeHowItWorks = () => {
    if (onSeeHowItWorks) return onSeeHowItWorks();
    const target =
      document.getElementById("how-it-works") ||
      document.getElementById("howItWorks") ||
      document.getElementById("about");
    if (target) target.scrollIntoView({ behavior: "smooth", block: "start" });
    else window.location.assign("/how-it-works");
  };

  return (
    <section id="bot-hero" ref={heroRef} className={`${styles.wrap} ${isOverlayPhase ? styles.overlayPhase : ""}`} aria-label="Hero">
      <div className={styles.inner}>
        <div className={`${styles.stage} ${isOverlayPhase ? styles.stageFill : ""}`}>
        <AnimatePresence mode="wait">
          {/* Phase 0: Chaos */}
          {phase === 0 && (
            <motion.div
              key={`p0-${seq}`}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.8 }}
              className={`${styles.phase} ${styles.phaseOverlay}`}
            >
              <motion.h1
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.3 }}
                className={styles.title}
              >
                Chaos in today&apos;s professional life
              </motion.h1>

              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.8, delay: 0.8 }}
                className={styles.chaosList}
              >
                {chaosWords.map((word, index) => (
                  <motion.div
                    key={word}
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{
                      duration: CHAOS_ANIM,
                      delay: CHAOS_BASE_DELAY + index * CHAOS_STEP,
                      type: "spring",
                      stiffness: 220,
                      damping: 22,
                    }}
                    className={styles.chaosItem}
                  >
                    {word}
                  </motion.div>
                ))}
              </motion.div>
            </motion.div>
          )}

          {/* Phase 1: NO MORE (white) */}
          {phase === 1 && (
            <motion.div
              key={`p1-${seq}`}
              initial={{ opacity: 0}}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0}}
              transition={{ duration: 0.45 }}
              className={`${styles.phase} ${styles.phaseOverlay}`}
            >
              <div className={styles.noMoreRow}>
                <motion.span
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.3, delay: 0.2 }}
                  className={styles.noMoreWord}
                >
                  NO
                </motion.span>
                <motion.span
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1}}
                  transition={{ duration: 0.3, delay: 0.5 }}
                  className={`${styles.noMoreWord} ${styles.noMoreGap}`}
                >
                  MORE
                </motion.span>
              </div>
            </motion.div>
          )}

          {/* Phase 2: Chimp intro */}
          {phase === 2 && (
            <motion.div
              key={`p2-${seq}`}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.45 }}
              className={styles.phase}
            >
              {/* Pill + heading + subcopy */}
              <motion.div
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: 0.2 }}
                className={styles.block}
              >
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.5, delay: 0.35 }}
                  className={styles.pill}
                >
                  <span className={styles.accent}>
                    <Sparkles className={styles.sparkIcon} />
                  </span>
                  <span className={styles.accentStrong}>Introducing Chimp</span>
                </motion.div>

                <motion.h2
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.7, delay: 0.5 }}
                  className={styles.h2}
                >
                  Your AI-native career sidekick designed for the{" "}
                  <span className={styles.accent}>next generation</span> of professionals
                </motion.h2>

                <motion.p
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.6, delay: 0.65 }}
                  className={`${styles.copy} ${styles.subcopy}`}
                >
                  A proactive, non-social, hyper-personalized career OS that gives you
                  direction before you even know what to ask.
                </motion.p>
              </motion.div>

              {/* Benefits grid */}
              <motion.div
                initial={{ opacity: 0, y: 18 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.9 }}
                className={styles.benefitsGrid}
              >
                {[
                  { title: "Clear Direction", desc: "Personalized feed through your CV. No scrolling." },
                  { title: "Continuous Growth", desc: "AI predicts what you need before you ask." },
                  { title: "Trusted Companion", desc: "Built entirely around your career goals." },
                ].map((b, i) => (
                  <motion.div
                    key={b.title}
                    initial={{ opacity: 0, y: 12, scale: 0.98 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    transition={{ duration: 0.5, delay: 1.05 + i * 0.12, type: "spring", stiffness: 160, damping: 20 }}
                    className={styles.benefitCard}
                  >
                    <h3 className={`${styles.benefitTitle} ${styles.accent}`}>{b.title}</h3>
                  </motion.div>
                ))}
              </motion.div>

              {/* CTAs using your existing button styles */}
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 1.5 }}
                className={styles.actions}
              >
                <button className={styles.primaryBtn} onClick={handleGetStarted}>
                  Get Started
                  <ArrowRight className={styles.arrowIcon} />
                </button>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
        </div>
      </div>
    </section>
  );
}
